import { useEffect } from "react";
import { signal } from "@preact/signals-react";
import { registerSW } from "virtual:pwa-register";

// const PWA_ID = "https://lography.uzoo.in/main";

export const pwaStatus = signal({
  isOffline: false,
  needsRefresh: false,
  isInstalled: false,
  isStandaloneMode: false,
  canInstall: false,
  deferredPrompt: null as any,
  registration: null as ServiceWorkerRegistration | null,
  updateSW: null as ((reloadPage?: boolean) => Promise<void>) | null,
});

export function PWAController({
  intervalMS = 60 * 60 * 1000,
  useCustomUI = false,
}: {
  intervalMS?: number;
  useCustomUI?: boolean; // 커스텀 UI 사용 여부
}) {
  useEffect(() => {
    const checkInstalled = async () => {
      const { isInstalled, isStandalone } = await isPWAInstalledAndStandalone();
      pwaStatus.value = {
        ...pwaStatus.value,
        isInstalled: isInstalled,
        isStandaloneMode: isStandalone,
      };
    };
    checkInstalled();

    // Install 프롬프트 처리
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      pwaStatus.value = {
        ...pwaStatus.value,
        isInstalled: false,
        canInstall: true,
        deferredPrompt: e,
      };
    });
    // Install 완료 처리
    window.addEventListener("appinstalled", () => {
      pwaStatus.value = {
        ...pwaStatus.value,
        isInstalled: true,
        canInstall: false,
        deferredPrompt: null,
      };
    });
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    // 서비스 워커 등록
    console.info(
      `[PWA] PWAController: intervalMS(${intervalMS}), useCustomUI(${useCustomUI})`,
    );

    let intervalId: NodeJS.Timeout;

    const updateSW = registerSW({
      onRegisteredSW: async (swUrl, registration) => {
        if (!registration) return;

        pwaStatus.value = {
          ...pwaStatus.value,
          registration,
          updateSW,
        };
        console.log("[PWA] SW registered:", registration);

        intervalId = setInterval(async () => {
          try {
            if (registration.installing || !navigator) return;
            if ("connection" in navigator && !navigator.onLine) return;

            const response = await fetch(swUrl, {
              cache: "no-store",
              headers: {
                cache: "no-store",
                "cache-control": "no-cache",
              },
            });

            if (response?.status === 200) {
              await registration.update();
            }
          } catch (error) {
            console.error("[PWA] Update check failed:", error);
          }
        }, intervalMS);
      },
      onRegisterError: (error) => {
        console.error("[PWA] Registration failed:", error);
      },
      onNeedRefresh: () => {
        pwaStatus.value = {
          ...pwaStatus.value,
          needsRefresh: true,
        };

        // 커스텀 UI를 사용하지 않을 때만 기본 confirm 창 표시
        if (!useCustomUI) {
          const userConfirmed = window.confirm(
            "새로운 버전이 나왔습니다. 업데이트하시겠습니까?",
          );

          if (userConfirmed) {
            // updateSW(true)
            //   .then(() => {
            //     // 업데이트가 완료된 후 needsRefresh를 false로 설정
            //     pwaStatus.value = {
            //       ...pwaStatus.value,
            //       needsRefresh: false,
            //     };
            //   })
            //   .catch((error) => {
            //     console.error("[PWA] Update failed:", error);
            //   });
            updateSW(false)
              .then(() => {
                location.replace(location.pathname);
              })
              .catch((error) => {
                console.error("[PWA] Update failed:", error);
              });
          }
        }
      },
      onOfflineReady: () => {
        pwaStatus.value = {
          ...pwaStatus.value,
          isOffline: true,
        };
        console.log("[PWA] App ready for offline use");
      },
    });

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    // 앱이 시작될 때마다 서비스워커 초기화 메시지 전송
    sendMessageToServiceWorker({
      type: "INITIALIZE_WORKER",
      timestamp: Date.now(),
    });
    // if ("serviceWorker" in navigator) {
    //   navigator.serviceWorker.ready.then((registration) => {
    //     registration.active?.postMessage({
    //       type: "INITIALIZE_WORKER",
    //       timestamp: Date.now(),
    //     });
    //   });
    // }
  }, []);

  const onFocus = () =>
    sendMessageToServiceWorker({
      type: "CLIENT_FOCUS",
      timestamp: Date.now(),
    });
  const onBlur = () =>
    sendMessageToServiceWorker({
      type: "CLIENT_BLUR",
      timestamp: Date.now(),
    });

  useEffect(() => {
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  return null;
}

export const sendMessageToServiceWorker = async (message: any) => {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage(message);
    } else {
      console.error("[PWA] Service worker is not active");
    }
  } else {
    console.error("[PWA] Service worker is not supported");
  }
};

export async function promptInstall() {
  const { deferredPrompt } = pwaStatus.value;
  if (!deferredPrompt) return;
  deferredPrompt.prompt();
  const result = await deferredPrompt.userChoice;

  // 사용자가 설치를 승인하든 취소하든 상관없이 deferredPrompt는 비활성화된다.
  pwaStatus.value = {
    ...pwaStatus.value,
    canInstall: false,
    deferredPrompt: null,
  };
  return result.outcome === "accepted";
}

export async function isPWAInstalledAndStandalone() {
  try {
    const nav = window.navigator as any; // 필요한 속성 접근을 위해 임시 타입 정의

    // --- 1. 독립 실행 모드 (isStandalone) 확인 ---
    // PWA가 현재 설치된 앱처럼 별도의 창/앱 뷰로 실행 중인지 체크합니다.
    let isStandalone = false;

    // 1-1. 미디어 쿼리 체크 (Chromium, Firefox 등 대부분의 브라우저)
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches // minimal-ui 모드 추가
    ) {
      isStandalone = true;
    }

    // 1-2. 구형 iOS Safari 체크 (Add to Home Screen)
    if ("standalone" in nav && nav.standalone === true) {
      isStandalone = true;
    }

    // --- 2. 설치 여부 (isInstalled) 확인 - getInstalledRelatedApps API 사용 ---
    if ("getInstalledRelatedApps" in nav) {
      try {
        const apps = await (navigator as any).getInstalledRelatedApps();

        // 반환된 앱 목록에서 현재 PWA의 manifest.json URL과 일치하는 항목을 찾습니다.
        // 이 URL은 Manifest 파일의 related_applications에 정의된 URL과 동일해야 합니다.
        // 플랫폼이 'webapp'이고 URL에 'lography.uzoo.in/manifest.json'이 포함된 경우를 확인합니다.
        const isInstalled = apps.some(
          (app: any) =>
            app.platform === "webapp" &&
            app.url.includes("lography.uzoo.in/manifest.webmanifest"),
        );

        console.log("[PWA] Installed Apps: ", apps);
        console.log(`[PWA] isInstalled (by URL check): ${isInstalled}`);

        if (isInstalled && isStandalone) {
          // PWA가 설치되어 있고 독립 실행 모드로 정상 실행 중 -> 안내 불필요
          console.log("[PWA] PWA가 정상적으로 실행 중입니다.");
        } else if (isInstalled && !isStandalone) {
          // PWA가 설치되어 있지만 브라우저 탭으로 열림
          console.warn(
            "[PWA] PWA가 설치되어 있지만 브라우저 탭으로 열렸습니다. 브라우저 설정을 확인해주세요.",
          );
        } else {
          // PWA가 설치되어 있지 않음
          console.warn(
            "[PWA] PWA를 설치하면 더 편리하게 사용할 수 있습니다. 설치하시겠습니까?",
          );
        }

        return {
          isInstalled,
          isStandalone,
        };
      } catch (error) {
        console.warn("[PWA] PWA 설치 여부 확인 중 오류:", error);
        return {
          isInstalled: false,
          isStandalone,
          error,
        };
      }
    } else {
      console.warn(
        "[PWA] 이 브라우저는 getInstalledRelatedApps를 지원하지 않습니다.",
      );
      return {
        isInstalled: false,
        isStandalone,
        supported: false,
      };
    }
  } catch (error) {
    console.error("[PWA] PWA 상태 확인 중 예상치 못한 오류:", error);
    return {
      isInstalled: false,
      isStandalone: false,
      error,
    };
  }
}
